import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Menu,
  ListSeparator,
  Input,
  Button,
  Caption1,
  TrashIcon,
  IconButton,
  CalendarIcon,
  Tooltip,
  InfoSmallIcon,
} from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useFocusInvalidField } from "~/components/Hooks/useFocusInvalidField";

const Target = forwardRef(({ onClick, open, dueOn }, ref) => {
  const { t } = useTranslation();

  const triggerText = useMemo(() => {
    if (+dueOn > 0) {
      return t("{{dueOn}}. day", { dueOn });
    }

    return <CalendarIcon />;
  }, [dueOn, t]);

  const tooltipText = useMemo(() => {
    return +dueOn > 0 ? t("Change Due Date") : t("Due Date");
  }, [dueOn, t]);

  return (
    <Tooltip title={tooltipText}>
      <IconButton
        ref={ref}
        variant="text gray"
        className={classNames("tw-ml-2 show-on-hover", { "tw-opacity-100": open || dueOn > 0, "tw-w-16": dueOn > 0 })}
        onClick={onClick}
        active={open}
      >
        {triggerText}
      </IconButton>
    </Tooltip>
  );
});

Target.propTypes = {
  onClick: PropTypes.func,
  className: PropTypes.string,
  open: PropTypes.bool,
  dueOn: PropTypes.number,
};

Target.displayName = "Target";

export const TemplateSubtaskDueOnForm = ({ dueOn, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dueIn, setDueIn] = useState(dueOn);
  const [error, setError] = useState({});
  const { fieldRef, focusFirstInvalidField } = useFocusInvalidField();

  const onInputChange = useCallback((e) => {
    setError({});
    setDueIn(e.target.value);
  }, []);

  useEffect(() => {
    setDueIn(dueOn);
  }, [dueOn]);

  const onTrashIconClick = useCallback(() => {
    onChange({ due_on: 0 });
    setOpen(false);
  }, [onChange]);

  const handleChange = useCallback(() => {
    if (dueIn > 365 || dueIn < 1) {
      setError({ due_in: t("Value must be between 1 and 365") });
      return focusFirstInvalidField({ due_in: t("Value must be between 1 and 365") }, false);
    }
    onChange({ due_on: dueIn });
    setOpen(false);
  }, [dueIn, focusFirstInvalidField, onChange, t]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDueIn(dueOn ?? "");
    setError({});
    setOpen(false);
  }, [dueOn]);

  return (
    <Menu
      open={open}
      target={
        <Target
          dueOn={dueOn}
          onClick={handleOpen}
          open={open}
        />
      }
      className="tw-px-4 tw-py-3 tw-w-72"
      onClose={handleCancel}
    >
      <Caption1 className="tw-flex tw-gap-1 tw-items-center">
        {t("Due on")}{" "}
        <Input
          id="due_in"
          name="dueIn"
          data-testid="due-date"
          size="small"
          type="number"
          placeholder={0}
          max={365}
          value={dueIn > 0 ? dueIn : ""}
          onChange={onInputChange}
          className={classNames("input-without-arrows", {
            "tw-w-16": error.due_in,
            "tw-w-11": !error.due_in,
          })}
          autoFocus
          invalid={error.due_in}
          endAdornment={
            error.due_in ? (
              <Tooltip title={error.due_in}>
                <InfoSmallIcon
                  fill="var(--red-alert)"
                  className="tw-cursor-help tw-mr-1"
                />
              </Tooltip>
            ) : null
          }
          ref={(el) => (fieldRef.current.due_in = el)}
        />{" "}
        {t("day of project")}
      </Caption1>
      <ListSeparator className="tw-mx-0" />
      <div className="tw-flex tw-justify-between tw-items-center">
        <div className="tw-flex">
          <Button
            size="small"
            type="button"
            data-testid="set-button"
            onClick={handleChange}
            className="tw-mr-3"
          >
            {t("Set")}
          </Button>
          <Button
            size="small"
            type="button"
            onClick={handleCancel}
            variant="secondary"
          >
            {t("Cancel")}
          </Button>
        </div>
        <IconButton
          type="button"
          variant="text gray"
          onClick={onTrashIconClick}
        >
          <TrashIcon />
        </IconButton>
      </div>
    </Menu>
  );
};

TemplateSubtaskDueOnForm.propTypes = {
  dueOn: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
};
